import React, { useState } from 'react';
import './ActivitySelectionList.css';
import { ChevronUp, ChevronDown } from './Icons';

const ActivitySelectionList = ({
  activities,
  selectedIds,
  onSelectionChange,
  onPlotSelected,
  bookmarks,
  onFlagActivity,
  onSetActivityNote,
  onFlagRider,
  onSetRiderAlias,
  onSetRiderNote,
}) => {
  const [sortBy, setSortBy] = useState('createdAt'); // 'createdAt', 'pointCount', 'userId'
  const [sortDesc, setSortDesc] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [editingNote, setEditingNote] = useState(null); // activityId being edited
  const [noteText, setNoteText] = useState('');
  const [editingRider, setEditingRider] = useState(null); // userId being edited
  const [riderAlias, setRiderAlias] = useState('');
  const [riderNote, setRiderNote] = useState('');

  // Get unique users
  const uniqueUsers = [...new Set(activities.map(a => a.userId))].filter(Boolean);

  // Filter and sort activities
  const { processedActivities, totalPages, totalItems } = React.useMemo(() => {
    let filtered = activities;
    
    // Filter by user if selected
    if (filterUser) {
      filtered = filtered.filter(a => a.userId === filterUser);
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'createdAt':
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
        case 'pointCount':
          aVal = a.pointCount || 0;
          bVal = b.pointCount || 0;
          break;
        case 'userId':
          aVal = a.userId || '';
          bVal = b.userId || '';
          break;
        default:
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
      }
      
      if (aVal < bVal) return sortDesc ? 1 : -1;
      if (aVal > bVal) return sortDesc ? -1 : 1;
      return 0;
    });
    
    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    // Paginate
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);
    
    return {
      processedActivities: paginated,
      totalPages,
      totalItems
    };
  }, [activities, filterUser, sortBy, sortDesc, currentPage, itemsPerPage]);
  
  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterUser, sortBy, sortDesc, itemsPerPage]);

  const handleSelectAll = () => {
    const allIds = processedActivities.map(a => a.activityId);
    onSelectionChange(allIds);
  };

  const handleSelectNone = () => {
    onSelectionChange([]);
  };

  const handleToggle = (activityId) => {
    if (selectedIds.includes(activityId)) {
      onSelectionChange(selectedIds.filter(id => id !== activityId));
    } else {
      onSelectionChange([...selectedIds, activityId]);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const diffMs = new Date(end) - new Date(start);
    const minutes = Math.floor(diffMs / 60000);
    return `${minutes}m`;
  };

  const actBk = bookmarks?.activities || {};
  const riderBk = bookmarks?.riders || {};

  const openNoteEditor = (activityId) => {
    setEditingNote(activityId);
    setNoteText(actBk[activityId]?.note || '');
  };

  const saveNote = () => {
    if (editingNote) onSetActivityNote?.(editingNote, noteText);
    setEditingNote(null);
  };

  const openRiderEditor = (userId) => {
    setEditingRider(userId);
    setRiderAlias(riderBk[userId]?.alias || '');
    setRiderNote(riderBk[userId]?.note || '');
  };

  const saveRider = () => {
    if (editingRider) {
      onSetRiderAlias?.(editingRider, riderAlias);
      onSetRiderNote?.(editingRider, riderNote);
    }
    setEditingRider(null);
  };

  return (
    <div className="activity-selection-list">
      <div className="list-header">
        <h2>Activity Selection</h2>
        <div className="list-controls">
          <div className="control-group">
            <label htmlFor="filter-user">Filter User:</label>
            <select id="filter-user" name="filterUser" value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
              <option value="">All Users</option>
              {uniqueUsers.map(userId => (
                <option key={userId} value={userId}>
                  {userId}
                </option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="sort-by">Sort By:</label>
            <select id="sort-by" name="sortBy" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="createdAt">Date Created</option>
              <option value="pointCount">Point Count</option>
              <option value="userId">User ID</option>
            </select>
            <button 
              className="sort-direction-btn"
              onClick={() => setSortDesc(!sortDesc)}
              title={sortDesc ? 'Descending' : 'Ascending'}
              aria-label={sortDesc ? 'Sort Descending' : 'Sort Ascending'}
            >
              {sortDesc ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>
        </div>
      </div>

      <div className="selection-actions">
        <button onClick={handleSelectAll} className="action-btn">
          Select All ({processedActivities.length})
        </button>
        <button onClick={handleSelectNone} className="action-btn">
          Select None
        </button>
        <div className="selected-count">
          {selectedIds.length} selected
        </div>
        <button 
          onClick={onPlotSelected}
          className="plot-btn"
          disabled={selectedIds.length === 0}
        >
          Plot Selected Activities &rarr;
        </button>
      </div>

      <div className="activities-table-container">
        {processedActivities.length === 0 ? (
          <div className="empty-state">
            <p>No activities found. Use the query form above to fetch activities.</p>
          </div>
        ) : (
          <table className="activities-table">
            <thead>
              <tr>
                <th className="col-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === processedActivities.length && processedActivities.length > 0}
                    onChange={() => {
                      if (selectedIds.length === processedActivities.length) {
                        handleSelectNone();
                      } else {
                        handleSelectAll();
                      }
                    }}
                  />
                </th>
                <th className="col-flag" title="Flag / bookmark">⚑</th>
                <th className="col-activity-id">Activity ID</th>
                <th className="col-user">User</th>
                <th className="col-date">Created</th>
                <th className="col-time">Time</th>
                <th className="col-points">Points</th>
                <th className="col-duration">Dur</th>
                <th className="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {processedActivities.map((activity) => {
                const abk = actBk[activity.activityId] || {};
                const rbk = riderBk[activity.userId] || {};
                const isFlagged = !!abk.flagged;
                const hasNote = !!abk.note;
                const isRiderFlagged = !!rbk.flagged;

                return (
                  <tr
                    key={activity.activityId}
                    className={[
                      selectedIds.includes(activity.activityId) ? 'selected' : '',
                      isFlagged ? 'row-flagged' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleToggle(activity.activityId)}
                  >
                    <td className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(activity.activityId)}
                        onChange={() => handleToggle(activity.activityId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="col-flag" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`flag-btn ${isFlagged ? 'flag-btn-on' : ''}`}
                        title={isFlagged ? 'Remove flag' : 'Flag this activity'}
                        onClick={() => onFlagActivity?.(activity.activityId, !isFlagged)}
                      >⚑</button>
                    </td>
                    <td className="col-activity-id" title={activity.activityId}>
                      {activity.activityId}
                    </td>
                    <td className="col-user" onClick={(e) => e.stopPropagation()}>
                      <span
                        className={`rider-name ${isRiderFlagged ? 'rider-flagged' : ''}`}
                        title={`${activity.userId}\nClick to edit rider`}
                        onClick={() => openRiderEditor(activity.userId)}
                      >
                        {rbk.alias ? (
                          <><span className="rider-alias">{rbk.alias}</span></>
                        ) : (
                          <span className="rider-id">{activity.userId || 'N/A'}</span>
                        )}
                        {isRiderFlagged && <span className="rider-flag-dot" title="Rider flagged">⚑</span>}
                      </span>
                    </td>
                    <td className="col-date">
                      {formatDate(activity.createdAt)}
                    </td>
                    <td className="col-time">
                      {formatTime(activity.firstTimestamp || activity.createdAt)}
                    </td>
                    <td className="col-points">
                      {activity.pointCount || 0}
                    </td>
                    <td className="col-duration">
                      {formatDuration(activity.firstTimestamp, activity.lastTimestamp)}
                    </td>
                    <td className="col-notes" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`note-btn ${hasNote ? 'note-btn-has' : ''}`}
                        title={abk.note || 'Add note'}
                        onClick={() => openNoteEditor(activity.activityId)}
                      >
                        {hasNote ? '📝' : '✎'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Note editor modal */}
      {editingNote && (
        <div className="note-modal-overlay" onClick={() => setEditingNote(null)}>
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note-modal-header">
              <span>Note for activity</span>
              <span className="note-modal-id">{editingNote}</span>
            </div>
            <textarea
              className="note-modal-textarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add notes about this activity..."
              autoFocus
              rows={5}
            />
            <div className="note-modal-actions">
              <button className="action-btn" onClick={saveNote}>Save</button>
              <button className="action-btn" onClick={() => setEditingNote(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Rider editor modal */}
      {editingRider && (
        <div className="note-modal-overlay" onClick={() => setEditingRider(null)}>
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <div className="note-modal-header">
              <span>Rider</span>
              <span className="note-modal-id">{editingRider}</span>
            </div>
            <label className="note-modal-label">Alias / Display Name</label>
            <input
              className="note-modal-input"
              value={riderAlias}
              onChange={(e) => setRiderAlias(e.target.value)}
              placeholder="e.g. Zach, Alex..."
              autoFocus
            />
            <label className="note-modal-label">Notes</label>
            <textarea
              className="note-modal-textarea"
              value={riderNote}
              onChange={(e) => setRiderNote(e.target.value)}
              placeholder="Known issues, observations..."
              rows={4}
            />
            <div className="note-modal-actions">
              <button
                className={`action-btn ${riderBk[editingRider]?.flagged ? 'action-btn-flagged' : ''}`}
                onClick={() => onFlagRider?.(editingRider, !riderBk[editingRider]?.flagged)}
              >
                {riderBk[editingRider]?.flagged ? '⚑ Unflag Rider' : '⚑ Flag Rider'}
              </button>
              <button className="action-btn" onClick={saveRider}>Save</button>
              <button className="action-btn" onClick={() => setEditingRider(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {processedActivities.length > 0 && totalPages > 1 && (
        <div className="pagination-controls">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} activities
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="page-indicator">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Last
            </button>
          </div>
          <div className="items-per-page">
            <label htmlFor="items-per-page">Items per page:</label>
            <select
              id="items-per-page"
              name="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitySelectionList;

